import { useState } from 'react'

import {
  useCreateScheduleRuleApi,
  useDeleteScheduleRuleApi,
  useReviewTemplatesApi,
  useRuleBrandsApi,
  useRuleCategoriesApi,
  useRulePriceRangesApi,
  useScheduleRulesApi,
  useUpdateScheduleRuleApi
} from '@/common/api/modules/ugcReviewSetup'
import {
  FollowupTargetFilter,
  RuleAxis,
  RuleCondition,
  ScheduleRule
} from '@/common/api/params/ugcReviewSetup'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 送信タイミングと文面の設定（ugc_review_schedule_rules）。
 *
 * 以前は配信間隔が3か所（このルール／旧ルール表／テンプレート）にあり、
 * 実際に効くのはここだけだった。設定が分かれていると、片方を直して
 * 「変えたのに変わらない」が起きる。Step2-2 でここに一本化した。
 *
 * ルールは**優先度の高いものから当たった1つだけ**が効く。上から順に
 * 表示して、どれが商品全体の既定なのかを見えるようにしている。
 */

const input =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-2 py-1.5'

const AXIS_LABEL: Record<string, string> = {
  product_id: '商品',
  brand: 'ブランド',
  category: 'カテゴリ',
  product_tag: '商品タグ',
  product_icon: '商品アイコン',
  price_range: '価格帯',
  purchase_count: '購入回数',
  has_reviewed: 'レビュー投稿歴',
  order_amount: '注文金額',
  days_since_shipped: '出荷からの日数'
}

/** 条件1つを日本語にする。一覧で「何に当たるルールか」を読めるようにする */
const conditionText = (c: RuleCondition): string => {
  const axis = AXIS_LABEL[c.axis] ?? c.axis
  if (c.axis === 'has_reviewed') return c.op === 'is_false' ? 'レビュー未投稿' : 'レビュー投稿済み'
  if (c.axis === 'purchase_count')
    return c.op === 'lte' ? '初回購入' : `${axis} ${String(c.value)}回以上`
  if (c.axis === 'order_amount') return `${axis} ${Number(c.value).toLocaleString()}円以上`
  if (c.axis === 'days_since_shipped')
    return `${axis} ${String(c.value)}日${c.op === 'lte' ? '以内' : '以上'}`
  if (c.axis === 'price_range') {
    const v = (c.value ?? {}) as { min?: number; max?: number }
    return `${axis} ${v.min?.toLocaleString() ?? ''}〜${v.max?.toLocaleString() ?? ''}円`
  }
  const n = Array.isArray(c.value) ? c.value.length : 0
  const suffix = c.op === 'not_in' ? ' 以外' : ''
  return n > 1 ? `${axis} ${n}件${suffix}` : `${axis}${suffix}`
}

const TARGETS: { value: FollowupTargetFilter; label: string }[] = [
  { value: 'all', label: '全員' },
  { value: 'opened', label: '開いた人だけ' },
  { value: 'clicked', label: 'リンクを押した人だけ' },
  { value: 'not_opened', label: '開いていない人だけ' }
]

type Draft = {
  enabled: boolean
  initial_delay_days: string
  delay_hours: string
  followup1_delay_days: string
  followup2_delay_days: string
  initial_template_id: string
  followup_template_id: string
  followup_target_filter: FollowupTargetFilter
}

const toDraft = (r: ScheduleRule): Draft => ({
  enabled: r.enabled,
  initial_delay_days: String(r.initial_delay_days ?? 3),
  delay_hours: String(r.delay_hours ?? 0),
  followup1_delay_days: String(r.followup1_delay_days ?? 0),
  followup2_delay_days: String(r.followup2_delay_days ?? 0),
  initial_template_id: r.initial_template_id === null ? '' : String(r.initial_template_id),
  followup_template_id: r.followup_template_id === null ? '' : String(r.followup_template_id),
  followup_target_filter: r.followup_target_filter ?? 'all'
})

const MailScheduleEditor = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = useScheduleRulesApi({ site_id: siteId })
  const { data: templateData } = useReviewTemplatesApi({ site_id: siteId })
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  // ルール追加フォーム。対象（セグメント）はW2マスタ由来の一覧から選ぶ
  const [adding, setAdding] = useState(false)
  // 商品の条件。'none' は「商品では絞らない」（顧客条件だけのルールが作れる）
  const [newType, setNewType] = useState<
    'none' | 'brand' | 'category' | 'price_range' | 'product_group'
  >('brand')
  const [newKey, setNewKey] = useState('')
  const [newProductIds, setNewProductIds] = useState('')
  // 顧客・注文の条件。商品条件と組み合わせると「ブランドA かつ 初回購入者」になる
  const [newCustomer, setNewCustomer] = useState<
    'none' | 'first_time' | 'repeat' | 'purchase_count' | 'no_review' | 'reviewed' | 'order_amount'
  >('none')
  const [newCustomerValue, setNewCustomerValue] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const { data: brands } = useRuleBrandsApi(adding ? { site_id: siteId } : undefined)
  const { data: categories } = useRuleCategoriesApi(adding ? { site_id: siteId } : undefined)
  const { data: priceRanges } = useRulePriceRangesApi(adding ? { site_id: siteId } : undefined)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const { trigger: createRule, isMutating: creating } = useCreateScheduleRuleApi({
    onSuccess: () => {
      setAdding(false)
      setNewKey('')
      setNewProductIds('')
      setNewCustomer('none')
      setNewCustomerValue('')
      setNewLabel('')
      setError(null)
      mutate()
    },
    onError: (e) => setError(e instanceof Error ? e.message : '追加できませんでした')
  })
  const { trigger: deleteRule } = useDeleteScheduleRuleApi({
    onSuccess: () => {
      setDeletingId(null)
      mutate()
    },
    onError: () => setDeletingId(null)
  })

  /** 商品の条件を1つ組み立てる。選んでいなければ null（商品では絞らない） */
  const buildProductCondition = (): RuleCondition | null => {
    if (newType === 'product_group') {
      const ids = newProductIds
        .split(/[\s,\u3001]+/)
        .map((x) => x.trim())
        .filter(Boolean)
      if (ids.length === 0) return null
      return { axis: 'product_id', op: 'in', value: ids }
    }
    if (newType === 'none' || !newKey) return null
    if (newType === 'price_range') {
      const range = priceRanges?.ranges.find((x) => x.key === newKey)
      if (!range) return null
      return { axis: 'price_range', op: 'between', value: { min: range.min, max: range.max } }
    }
    return { axis: newType as RuleAxis, op: 'in', value: [newKey] }
  }

  /** 顧客・注文の条件。「指定しない」なら null */
  const buildCustomerCondition = (): RuleCondition | null => {
    const n = Number(newCustomerValue)
    switch (newCustomer) {
      case 'first_time':
        return { axis: 'purchase_count', op: 'lte', value: 1 }
      case 'repeat':
        return { axis: 'purchase_count', op: 'gte', value: 2 }
      case 'purchase_count':
        return Number.isFinite(n) && n >= 1 ? { axis: 'purchase_count', op: 'gte', value: n } : null
      case 'no_review':
        return { axis: 'has_reviewed', op: 'is_false' }
      case 'reviewed':
        return { axis: 'has_reviewed', op: 'is_true' }
      case 'order_amount':
        return Number.isFinite(n) && n >= 0 ? { axis: 'order_amount', op: 'gte', value: n } : null
      default:
        return null
    }
  }

  /** 条件から既定のルール名を作る。運用者が名前を付けなかったとき用 */
  const autoLabel = (conds: RuleCondition[]): string => {
    if (conds.length === 0) return 'サイト全体（既定）'
    return conds
      .map((c) => {
        const first = Array.isArray(c.value) ? String(c.value[0]) : ''
        if (c.axis === 'product_id') return `商品${(c.value as string[]).length}件`
        if (c.axis === 'brand')
          return brands?.brands.find((b) => b.brand_id === first)?.brand_name ?? 'ブランド'
        if (c.axis === 'category')
          return (
            categories?.categories.find((x) => x.category_id === first)?.category_name ?? 'カテゴリ'
          )
        if (c.axis === 'price_range') return '価格帯'
        if (c.axis === 'purchase_count')
          return c.op === 'lte' ? '初回購入者' : `購入${String(c.value)}回以上`
        if (c.axis === 'has_reviewed')
          return c.op === 'is_false' ? 'レビュー未投稿' : 'レビュー投稿済み'
        if (c.axis === 'order_amount') return `注文${Number(c.value).toLocaleString()}円以上`
        return c.axis
      })
      .join(' \u00d7 ')
  }

  const onAdd = () => {
    // 選んだのに値が空、という取りこぼしはここで気づかせる
    if (newType === 'product_group' && !buildProductCondition()) {
      setError('商品IDを1つ以上入力してください')
      return
    }
    if (newType !== 'none' && newType !== 'product_group' && !newKey) {
      setError('対象を選択してください')
      return
    }
    if (
      (newCustomer === 'purchase_count' || newCustomer === 'order_amount') &&
      !buildCustomerCondition()
    ) {
      setError('数値を入力してください')
      return
    }

    const conds = [buildProductCondition(), buildCustomerCondition()].filter(
      (c): c is RuleCondition => c !== null
    )
    createRule({
      site_id: siteId,
      conditions: conds,
      rule_label: newLabel.trim() || autoLabel(conds)
    })
  }

  const { trigger: save, isMutating: saving } = useUpdateScheduleRuleApi({
    onSuccess: () => {
      setEditingId(null)
      setDraft(null)
      setError(null)
      mutate()
    },
    onError: (e) => setError(e instanceof Error ? e.message : '保存できませんでした')
  })

  const templates = templateData?.templates ?? []
  const templateName = (id: number | null) => {
    if (id === null) return '既定の文面'
    return templates.find((t) => t.id === id)?.template_name ?? `文面 #${id}（見つかりません）`
  }

  const rules = data?.rules ?? []

  const startEdit = (r: ScheduleRule) => {
    setEditingId(r.id)
    setDraft(toDraft(r))
    setError(null)
  }

  const onSave = (ruleId: number) => {
    if (!draft) return
    const num = (v: string, fallback: number) => {
      const n = Number(v)
      return Number.isFinite(n) && n >= 0 ? n : fallback
    }
    save({
      site_id: siteId,
      rule_id: ruleId,
      enabled: draft.enabled,
      initial_delay_days: num(draft.initial_delay_days, 3),
      delay_hours: num(draft.delay_hours, 0),
      followup1_delay_days: num(draft.followup1_delay_days, 0),
      followup2_delay_days: num(draft.followup2_delay_days, 0),
      initial_template_id: draft.initial_template_id === '' ? null : Number(draft.initial_template_id),
      followup_template_id:
        draft.followup_template_id === '' ? null : Number(draft.followup_template_id),
      followup_target_filter: draft.followup_target_filter
    })
  }

  return (
    <Area>
      <Heading subText='商品ごとに、いつ送るか・どの文面で送るかを決めます。優先度の高いルールから、当たった1つだけが効きます。「サイト全体（既定）」はどれにも当たらなかった商品に使われます。'>
        送信タイミングと文面の設定
      </Heading>
      <AreaContent>
        <div className='mb-3'>
          <Button color={ButtonColor.SECONDARY} onClick={() => setAdding((v) => !v)}>
            {adding ? '追加をやめる' : 'ルールを追加する'}
          </Button>
        </div>

        {adding && (
          <div className='mb-4 flex flex-col gap-3 rounded-lg border border-border-brand px-4 py-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <Text size={12}>商品の条件</Text>
              <select
                className={input}
                value={newType}
                onChange={(e) => {
                  setNewType(e.target.value as typeof newType)
                  setNewKey('')
                }}
              >
                <option value='none'>指定しない（すべての商品）</option>
                <option value='brand'>ブランド</option>
                <option value='category'>カテゴリ</option>
                <option value='price_range'>価格帯</option>
                <option value='product_group'>商品グループ（商品IDを列挙）</option>
              </select>

              {newType === 'brand' && (
                <select className={input} value={newKey} onChange={(e) => setNewKey(e.target.value)}>
                  <option value=''>ブランドを選択</option>
                  {(brands?.brands ?? []).map((b) => (
                    <option key={b.brand_id} value={b.brand_id}>
                      {b.brand_name}
                    </option>
                  ))}
                </select>
              )}
              {newType === 'category' && (
                <select className={input} value={newKey} onChange={(e) => setNewKey(e.target.value)}>
                  <option value=''>カテゴリを選択</option>
                  {(categories?.categories ?? []).map((c) => (
                    <option key={c.category_id} value={c.category_id}>
                      {'\u3000'.repeat(c.depth)}
                      {c.category_name}
                    </option>
                  ))}
                </select>
              )}
              {newType === 'price_range' && (
                <select className={input} value={newKey} onChange={(e) => setNewKey(e.target.value)}>
                  <option value=''>価格帯を選択</option>
                  {(priceRanges?.ranges ?? []).map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}（{r.count.toLocaleString()}商品）
                    </option>
                  ))}
                </select>
              )}
            </div>

            {newType === 'product_group' && (
              <label className='flex flex-col gap-1'>
                <Text size={12}>商品ID（カンマ・空白区切り）</Text>
                <input
                  className={`${input} w-full`}
                  value={newProductIds}
                  placeholder='例）SKU001, SKU002'
                  onChange={(e) => setNewProductIds(e.target.value)}
                />
              </label>
            )}

            {/* 商品条件と組み合わせると「ブランドA かつ 初回購入者」になる。
                両方を満たした注文にだけ、このルールの日数・文面が使われる */}
            <div className='flex flex-wrap items-center gap-2'>
              <Text size={12}>顧客・注文の条件</Text>
              <select
                className={input}
                value={newCustomer}
                onChange={(e) => {
                  setNewCustomer(e.target.value as typeof newCustomer)
                  setNewCustomerValue('')
                }}
              >
                <option value='none'>指定しない（すべての注文）</option>
                <option value='first_time'>初回購入の方</option>
                <option value='repeat'>2回目以降の方</option>
                <option value='purchase_count'>購入回数が指定回数以上</option>
                <option value='no_review'>まだレビューを書いていない方</option>
                <option value='reviewed'>レビューを書いたことがある方</option>
                <option value='order_amount'>注文金額が指定額以上</option>
              </select>

              {(newCustomer === 'purchase_count' || newCustomer === 'order_amount') && (
                <input
                  className={`${input} w-32`}
                  type='number'
                  min={newCustomer === 'purchase_count' ? 1 : 0}
                  value={newCustomerValue}
                  placeholder={newCustomer === 'purchase_count' ? '例）3' : '例）10000'}
                  onChange={(e) => setNewCustomerValue(e.target.value)}
                />
              )}
              {(newCustomer === 'purchase_count' || newCustomer === 'order_amount') && (
                <Text size={12} color={TextColor.SECONDARY}>
                  {newCustomer === 'purchase_count' ? '回以上' : '円以上'}
                </Text>
              )}
            </div>

            <label className='flex flex-col gap-1 md:max-w-md'>
              <Text size={12}>ルールの名前（任意。空欄なら条件から作ります）</Text>
              <input
                className={`${input} w-full`}
                value={newLabel}
                placeholder='例）新商品は早めに聞く'
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </label>

            <div className='flex items-center gap-3'>
              <Button
                color={ButtonColor.PRIMARY}
                loading={creating}
                disabled={creating}
                onClick={onAdd}
              >
                この対象でルールを追加
              </Button>
              <Text size={10} color={TextColor.TERTIARY}>
                追加後に間隔・文面・フォローアップ対象を編集できます
              </Text>
            </div>
          </div>
        )}

        {isLoading ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : rules.length === 0 ? (
          <Text size={13} color={TextColor.SECONDARY}>
            ルールがありません。この場合は出荷の3日後に送り、7日後と14日後にフォローアップします。
          </Text>
        ) : (
          <div className='flex flex-col gap-3'>
            {rules.map((r) => {
              const editing = editingId === r.id && draft !== null
              return (
                <div
                  key={r.id}
                  className='flex flex-col gap-2 rounded-lg border border-border-primary px-4 py-3'
                >
                  <div className='flex flex-wrap items-center gap-2'>
                    <Text size={13} weight='bold'>
                      {r.rule_label || (r.conditions.length ? '条件つきルール' : 'サイト全体（既定）')}
                    </Text>
                    {/* 条件をバッジで並べる。名前だけだと「何に当たるか」が分からない */}
                    {r.conditions.length === 0 ? (
                      <Badge size={10} badgeType={BadgeType.SECONDARY}>
                        すべての注文
                      </Badge>
                    ) : (
                      r.conditions.map((c, i) => (
                        <Badge key={i} size={10} badgeType={BadgeType.SECONDARY}>
                          {conditionText(c)}
                        </Badge>
                      ))
                    )}
                    {!r.enabled && (
                      <Badge size={10} badgeType={BadgeType.DISABLED}>
                        送らない
                      </Badge>
                    )}
                    <span className='ml-auto'>
                      {editing ? (
                        <span className='flex items-center gap-2'>
                          <Button
                            color={ButtonColor.PRIMARY}
                            loading={saving}
                            disabled={saving}
                            onClick={() => onSave(r.id)}
                          >
                            保存する
                          </Button>
                          <Button
                            color={ButtonColor.TERTIARY}
                            onClick={() => {
                              setEditingId(null)
                              setDraft(null)
                            }}
                          >
                            やめる
                          </Button>
                        </span>
                      ) : (
                        <span className='flex items-center gap-2'>
                          <Button color={ButtonColor.TERTIARY} onClick={() => startEdit(r)}>
                            編集
                          </Button>
                          {r.conditions.length > 0 && (
                            <Button
                              color={ButtonColor.TERTIARY}
                              disabled={deletingId === r.id}
                              loading={deletingId === r.id}
                              onClick={() => {
                                setDeletingId(r.id)
                                deleteRule({ site_id: siteId, rule_id: r.id })
                              }}
                            >
                              削除
                            </Button>
                          )}
                        </span>
                      )}
                    </span>
                  </div>

                  {editing && draft ? (
                    <div className='flex flex-col gap-3'>
                      <label className='flex items-center gap-2'>
                        <input
                          type='checkbox'
                          checked={draft.enabled}
                          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                        />
                        <Text size={12}>この対象にレビュー依頼を送る</Text>
                      </label>

                      <div className='flex flex-wrap items-center gap-2'>
                        <Text size={12}>初回は出荷から</Text>
                        <input
                          className={`${input} w-16`}
                          type='number'
                          min={0}
                          value={draft.initial_delay_days}
                          onChange={(e) => setDraft({ ...draft, initial_delay_days: e.target.value })}
                        />
                        <Text size={12}>日と</Text>
                        <input
                          className={`${input} w-16`}
                          type='number'
                          min={0}
                          max={23}
                          value={draft.delay_hours}
                          onChange={(e) => setDraft({ ...draft, delay_hours: e.target.value })}
                        />
                        <Text size={12}>時間あと</Text>
                      </div>

                      <div className='flex flex-wrap items-center gap-2'>
                        <Text size={12}>1回目のフォローアップは初回送信から</Text>
                        <input
                          className={`${input} w-16`}
                          type='number'
                          min={0}
                          value={draft.followup1_delay_days}
                          onChange={(e) =>
                            setDraft({ ...draft, followup1_delay_days: e.target.value })
                          }
                        />
                        <Text size={12}>日あと</Text>
                      </div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Text size={12}>2回目は1回目の送信から</Text>
                        <input
                          className={`${input} w-16`}
                          type='number'
                          min={0}
                          value={draft.followup2_delay_days}
                          onChange={(e) =>
                            setDraft({ ...draft, followup2_delay_days: e.target.value })
                          }
                        />
                        <Text size={12}>日あと</Text>
                        <Text size={10} color={TextColor.TERTIARY}>
                          0 にするとその回は送りません
                        </Text>
                      </div>

                      <div className='flex flex-wrap items-center gap-2'>
                        <Text size={12}>フォローアップを送る相手</Text>
                        <select
                          className={input}
                          value={draft.followup_target_filter}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              followup_target_filter: e.target.value as FollowupTargetFilter
                            })
                          }
                        >
                          {TARGETS.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <Text size={10} color={TextColor.TERTIARY}>
                          直前に送ったメールへの反応で判定します
                        </Text>
                      </div>

                      <div className='flex flex-wrap items-center gap-2'>
                        <Text size={12}>初回の文面</Text>
                        <select
                          className={input}
                          value={draft.initial_template_id}
                          onChange={(e) =>
                            setDraft({ ...draft, initial_template_id: e.target.value })
                          }
                        >
                          <option value=''>既定の文面</option>
                          {templates.map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              {t.template_name}
                            </option>
                          ))}
                        </select>
                        <Text size={12}>フォローアップの文面</Text>
                        <select
                          className={input}
                          value={draft.followup_template_id}
                          onChange={(e) =>
                            setDraft({ ...draft, followup_template_id: e.target.value })
                          }
                        >
                          <option value=''>既定の文面</option>
                          {templates.map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              {t.template_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {error && (
                        <Text size={12} color={TextColor.CRITICAL}>
                          {error}
                        </Text>
                      )}
                    </div>
                  ) : (
                    <Text size={12} color={TextColor.SECONDARY}>
                      {r.enabled
                        ? `出荷の${r.initial_delay_days}日${r.delay_hours ? `と${r.delay_hours}時間` : ''}あとに初回。` +
                          `フォローアップは${r.followup1_delay_days ? `${r.followup1_delay_days}日後` : 'なし'}` +
                          `／${r.followup2_delay_days ? `さらに${r.followup2_delay_days}日後` : 'なし'}。` +
                          `対象は${TARGETS.find((t) => t.value === r.followup_target_filter)?.label ?? '全員'}。` +
                          `文面は初回「${templateName(r.initial_template_id)}」／フォロー「${templateName(r.followup_template_id)}」。`
                        : 'この対象にはレビュー依頼を送りません。'}
                    </Text>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default MailScheduleEditor
